/** @format */

import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { CookieService, HttpService, Logger } from '@shared/data-access/services';

import {
	PDPRangeRequestBody,
	ProductDetailsByUrlResponse,
	SkuEDDResponse
} from '@pdp/data-access/interfaces';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import {
	Chain,
	Environment,
	MultipleProductsByEcodeParams,
	MultipleProductsByEcodeResponse,
	NestProductResponse,
	ProductSkuCatalogDetails,
	PromotionDetailsMapV1,
	PromotionDetailsMapV2,
	PromotionResponseV1,
	PromotionResponseV2,
	RatingSummaryResponse
} from '@shared/data-access/interfaces';

@Injectable({
	providedIn: 'root'
})
export class ProductDao {
	readonly REVIEW_SUMMARY_BY_PRODUCT_STATE_KEY_PREFIX = 'review-summary-';

	constructor(
		private chain: Chain,
		private environment: Environment,
		private http: HttpClient,
		@Inject(PLATFORM_ID) protected platformId: object,
		private state: TransferState,
		private httpService: HttpService,
		private cookieService: CookieService,
		private logger: Logger
	) {}

	/**
	 * @description
	 * Returns a Product by product ID (eCode or catEntryId)
	 */
	public getAddOnSkuByEcode$(
		eCodeList: string,
		channel: string,
		retries = 0,
		version = 'v1',
		requestType?: string
	): Observable<NestProductResponse> {
		// Query String Parameters
		let httpParams = new HttpParams();

		// Set channel param
		httpParams = httpParams.set('channel', `${channel}`);

		if (requestType) {
			// Set channel param
			httpParams = httpParams.set('requestType', `${requestType}`);
		}

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Custom HTTP Headers
		httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

		// Get Origin Host Header
		const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
		if (origin) {
			httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
		}

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${this.environment.api.spring.microServiceProxy.product.timeout}`
			);
		}

		// HTTP Options
		const httpOptions: Parameters<typeof this.http.get>[1] = {
			headers: httpHeaders
		};

		// Check to see if any of the optional parameters have been set
		if (httpParams.keys().length > 0) {
			// Add the Query String Parameters to the HTTP Options
			httpOptions.params = httpParams;
		}

		const proxyPath = this.environment.api.nestjs.microServiceProxy.product.path;

		// Make the HTTP Request
		return this.http
			.get(
				`${proxyPath}${version}/store/${this.chain.storeId}/multiproducts/${eCodeList}`,
				httpOptions
			)
			.pipe(
				map((response: { data: NestProductResponse }) => response.data),
				retry(retries)
			);
	}

	/**
	 * @description
	 * Returns Promotion details for a given Product ID
	 */
	public getPromotionsByProductId$(
		catEntryId: string | number,
		channelId: string,
		skuIdList?: string | string[],
		useNewPricingService?: boolean,
		retries = 0
	): Observable<PromotionDetailsMapV1 | PromotionDetailsMapV2> {
		const path = useNewPricingService ? 'v2' : 'v1';
		const url = `${this.environment.pricing.path}${path}/${this.chain.storeId}`;

		let httpParams = new HttpParams();
		httpParams = httpParams.set('catEntryId', `${catEntryId}`);
		httpParams = httpParams.set('channelId', channelId);

		if (useNewPricingService) {
			httpParams = httpParams.set('skus', `${skuIdList}`);
		}

		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON,
			'x-CentraSite-APIKey': this.environment.apiTokens.esb.api2,
			Env: this.environment.pricing.esbEnvironmentHeader
		});

		// Get Origin Host Header
		const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
		if (origin) {
			httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
		}

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			params: httpParams
		};

		// Make the HTTP Request
		return this.http.get(url, httpOptions).pipe(
			map((response: PromotionResponseV1 | PromotionResponseV2) => {
				let promoDetailsMap: PromotionDetailsMapV1 | PromotionDetailsMapV2;

				if ((response as PromotionResponseV1)?.data?.promoDetailsMap) {
					promoDetailsMap = (response as PromotionResponseV1).data.promoDetailsMap;
				}

				if ((response as PromotionResponseV2)?.data?.discountDetailsMap) {
					promoDetailsMap = (response as PromotionResponseV2).data.discountDetailsMap;
				}

				// Return the raw data
				return promoDetailsMap;
			}),
			retry(retries)
		);
	}

	/**
	 * @description
	 * Gets review data from BazaarVoice for a given product by ID
	 */
	public getReviewsByProductId$(
		productId: string,
		retries = 0
	): Observable<RatingSummaryResponse> {
		// Get unique key
		const STATE_KEY = makeStateKey<RatingSummaryResponse>(
			`${this.REVIEW_SUMMARY_BY_PRODUCT_STATE_KEY_PREFIX}${productId}`
		);

		// Attempt to get the state by key
		const stateData: RatingSummaryResponse = this.state.get(STATE_KEY, null);

		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return new Observable((observer) => {
				observer.next(stateData);
				observer.complete();
			});
		}

		// Query String Parameters
		const httpParams = new HttpParams()
			.set(
				'passkey',
				`${
					this.environment.thirdParty.bv.api[this.chain.chainIdentifierAbbr]
						.reviewSummaryPassKey
				}`
			)
			.set('apiVersion', `${this.environment.thirdParty.bv.api.revewSummaryApiVersion}`)
			.set('filter', `productId:${productId}`)
			.set('Include', 'Products')
			.set('Stats', 'Reviews');

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${DCSG_CONSTANTS.timeouts.short}`
			);
		}

		// HTTP Options
		const httpOptions: Parameters<typeof this.http.get>[1] = {
			headers: httpHeaders,
			params: httpParams
		};

		const url = `${this.environment.thirdParty.bv.api.proxyConfig.proxy.path}${this.environment.thirdParty.bv.api.reviewsUrl}`;

		// Make the HTTP Request
		return this.http.get(url, httpOptions).pipe(
			map((response: RatingSummaryResponse) => {
				// Set the state
				this.state.set(STATE_KEY, response);

				// Return the response
				return response;
			}),
			retry(retries)
		);
	}

	/**
	 * @description
	 * Submits user email and product details in order to set up a in stock reminder
	 */
	public setProductInStockReminder$(
		userEmail: string,
		skuId: string,
		productTitle: string,
		productURL: string,
		imageURL: string,
		attributes: { label: string; value: string }[],
		storeNumber: string,
		storeName?: string,
		retries = 0
	): Observable<unknown> {
		// Standard HTTP Headers
		const httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON,
			partner_key: this.environment.apiTokens.inStockReminder.partner,
			secret_key: this.environment.apiTokens.inStockReminder.key
		});
		const embededdedJsonBody = {
			title: productTitle,
			productURL,
			imageURL,
			attributes,
			chain: this.chain.chainIdentifierAbbr
		};

		if (storeName) {
			embededdedJsonBody['storeName'] = storeName;
		}

		// Request body
		const reqBody = {
			sku: skuId,
			email: userEmail,
			storeNumber,
			embeddedJson: JSON.stringify(embededdedJsonBody)
		};

		if (storeNumber === undefined) {
			const storeCookies = {
				setStoreCookie: this.cookieService.get('setStoreCookie'),
				dcsg_ngx_cust_store: this.cookieService.get('DCSG_NGX_CUST_STORE')
			};

			this.logger.log(storeCookies);
		}

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders
		};

		// Make the HTTP Request
		return this.http
			.post(
				`${this.environment.api.inStockReminder.baseUrl}v1/instockreminder`,
				reqBody,
				httpOptions
			)
			.pipe(retry(retries));
	}

	/**
	 * @description
	 * Gets the Product details from the Product Quick View API
	 */
	public getProductByUrl$(
		url: string,
		partNumber: string,
		channel: string,
		clearanceFlag?: string,
		antiClearanceFlag?: string,
		categoryId?: string,
		productParamType?: string,
		retries = 0
	): Observable<ProductDetailsByUrlResponse> {
		// Query String Parameters
		let httpParams = new HttpParams();

		// Set channel param
		httpParams = httpParams.set('channel', `${channel}`);

		// set inventory param
		httpParams = httpParams.set('inventory', 'true');

		// set active param
		httpParams = httpParams.set('active', 'true');

		// set active param
		httpParams = httpParams.set('skus', 'true');

		// Set url param
		httpParams = httpParams.set('url', `${url}`);

		// Set partNumber param
		httpParams = httpParams.set('partNumber', `${partNumber}`);

		// Check to see if the clearance flag is set
		if (typeof clearanceFlag !== 'undefined') {
			httpParams = httpParams.set('clearance', `${clearanceFlag}`);
		}

		// Check to see if the anticlearance flag is set
		if (typeof antiClearanceFlag !== 'undefined') {
			httpParams = httpParams.set('antiClearance', `${antiClearanceFlag}`);
		}

		// Check to see if category ID was provided
		if (typeof categoryId === 'string' && categoryId.trim() !== '') {
			httpParams = httpParams.set('categoryId', `${categoryId.trim()}`);
		}

		// Check to see if the parameter type has been specified
		if (typeof productParamType === 'string' && productParamType.trim() !== '') {
			httpParams = httpParams.set('searchType', `${productParamType.toLowerCase().trim()}`);
		}

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Custom HTTP Headers
		httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

		// Get Origin Host Header
		const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
		if (origin) {
			httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
		}

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${this.environment.api.spring.microServiceProxy.product.timeout}`
			);
		}

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			params: httpParams,
			withCredentials: true
		};

		// Check to see if any of the optional parameters have been set
		if (httpParams.keys().length > 0) {
			// Add the Query String Parameters to the HTTP Options
			httpOptions.params = httpParams;
		}

		const seoUrl = this.environment.api.seoByChain;

		// Make the HTTP Request
		return this.http
			.get(`${seoUrl}catalog-productdetails/v4/byURL/${this.chain.storeId}`, httpOptions)
			.pipe(
				map((response: ProductDetailsByUrlResponse) => {
					// Return the raw data
					return response;
				}),
				retry(retries)
			);
	}

	public getProductByEcodeV4$(
		productId: string,
		checkClearance = false,
		retries = 0
	): Observable<ProductDetailsByUrlResponse> {
		let params = new HttpParams();
		params = params
			.set('id', productId)
			.set('inventory', 'true')
			.set('clearance', checkClearance);

		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Get Origin Host Header
		const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
		if (origin) {
			httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
		}

		const httpOptions = {
			httpHeaders,
			params,
			withCredentials: false
		};

		return this.http
			.get(
				`${this.environment.api.seo}catalog-productdetails/v4/byPartNumber/${this.chain.storeId}`,
				httpOptions
			)
			.pipe(
				map((response: ProductDetailsByUrlResponse) => {
					return response;
				}),
				retry(retries)
			);
	}

	/**
	 * Get the Sku EDD Data
	 * @param skuId - SkuId
	 * @returns - Sku EDD Data
	 */
	public getSkuDeliveryDate$(skuId: string): Observable<SkuEDDResponse> {
		// Standard HTTP Headers
		const httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON,
			'Content-Type': MediaType.APPLICATION_JSON,
			'Access-Control-Allow-Origin': '*',
			Pragma: 'no-cache',
			'X-TIMEOUT': DCSG_CONSTANTS.timeouts.short
		});

		// Request body.
		const reqBody: PDPRangeRequestBody = {
			consumingTeam: 'PDP',
			referer: window.location.href.split('?')[0],
			source: this.chain.metaCanonical,
			customerZipCode:
				this.cookieService.get('deliveryZip') || this.cookieService.get('whereabouts'),
			skus: [skuId]
		};

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders
		};

		// Make the HTTP Request
		return this.http
			.post(`${this.environment.api.edd.proxy.path}v2/edd-pdp/ranges`, reqBody, httpOptions)
			.pipe(
				map((response: SkuEDDResponse) => response),
				catchError(() => of(null))
			);
	}

	public getMultipleProductsByEcode$<
		T extends MultipleProductsByEcodeParams | undefined = undefined
	>(ecodes: string[], options?: T, retries = 0) {
		let params = new HttpParams();
		for (const param in options || {}) {
			params = params.set(param, options[param]);
		}
		params = params.set('ecode', ecodes.join(','));

		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Get Origin Host Header
		const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
		if (origin) {
			httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
		}

		const httpOptions = {
			httpHeaders,
			params,
			withCredentials: false
		};

		return this.http
			.get(
				`${this.environment.api.seo}catalog-productdetails/v4/store/${this.chain.storeId}/products`,
				httpOptions
			)
			.pipe(
				map((response: MultipleProductsByEcodeResponse<T>) => {
					return response;
				}),
				retry(retries)
			);
	}

	public getProductsBySkus$(skus: string[], retries = 0): Observable<ProductSkuCatalogDetails[]> {
		let params = new HttpParams();
		params = params.set('id', skus.join(','));

		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Get Origin Host Header
		const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
		if (origin) {
			httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
		}

		const httpOptions = {
			httpHeaders,
			params,
			withCredentials: false
		};

		return this.http
			.get(
				`${this.environment.api.seo}catalog-productdetails/v4/bySku/${this.chain.storeId}`,
				httpOptions
			)
			.pipe(
				map((response: ProductSkuCatalogDetails[]) => {
					return response;
				}),
				retry(retries)
			);
	}
}
